import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ShoppingBag, ArrowRight } from "lucide-react";
import { useAudio } from "@/context/AudioContext";

/**
 * Mobile-only sticky bottom bar that keeps merch one tap away while fans scroll.
 * - Shows after the user scrolls past the hero.
 * - Hides while the merch section is already in view (no redundancy).
 * - Sits above the PlayerBar when audio is playing (offset by 60px).
 */
export default function StickyBuyBar() {
  const [visible, setVisible] = useState(false);
  const audio = useAudio();
  const playerActive = audio && audio.currentIndex >= 0;

  useEffect(() => {
    let raf = 0;
    const evaluate = () => {
      raf = 0;
      const merch = document.querySelector('[data-testid="merch-section"]');
      const scrolled = window.scrollY > window.innerHeight * 0.6;
      let merchInView = false;
      if (merch) {
        const r = merch.getBoundingClientRect();
        merchInView = r.top < window.innerHeight * 0.6 && r.bottom > 0;
      }
      setVisible(scrolled && !merchInView);
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(evaluate);
    };
    evaluate();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  const go = () => {
    const el = document.querySelector('[data-testid="merch-section"]');
    if (!el) return;
    if (typeof window !== "undefined" && window.__lenis) {
      window.__lenis.scrollTo(el, { offset: -20 });
    } else {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          data-testid="sticky-buy-bar"
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
          className={`md:hidden fixed left-3 right-3 z-40 bg-black/90 backdrop-blur-xl border border-zinc-800 flex items-center justify-between gap-3 px-4 py-3 ${
            playerActive ? "bottom-[76px]" : "bottom-3"
          }`}
        >
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-8 h-8 border border-zinc-800 flex items-center justify-center shrink-0">
              <ShoppingBag size={14} className="text-white" />
            </div>
            <div className="min-w-0">
              <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-500">Shop</p>
              <p className="font-display font-bold text-sm text-white truncate">Merch — from $20</p>
            </div>
          </div>
          <button
            data-testid="sticky-buy-cta"
            onClick={go}
            className="shrink-0 bg-white text-black px-4 py-2.5 font-mono uppercase tracking-widest text-[10px] hover:bg-zinc-200 transition-colors flex items-center gap-1"
          >
            View <ArrowRight size={12} />
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
